// Copyright (C) 2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { Config } from '@react-awesome-query-builder/antd';
import {
    DataType, GetDataTypeChinese, GetTaskStatusChinese, TaskStatus,
} from 'cvat-core/src/enums';
import asyncFetchUsers from 'components/resource-sorting-filtering/request-users';

export const config: Partial<Config> = {
    fields: {
        dimension: {
            label: '维度',
            type: 'select',
            operators: ['select_equals'],
            valueSources: ['value'],
            fieldSettings: {
                listValues: [
                    { value: '2d', title: '2D' },
                    { value: '3d', title: '3D' },
                ],
            },
        },
        status: {
            label: '状态',
            type: 'select',
            valueSources: ['value'],
            operators: ['select_equals', 'select_any_in', 'select_not_any_in'],
            fieldSettings: {
                listValues: [
                    { value: 'annotation', title: `${GetTaskStatusChinese(TaskStatus.ANNOTATION)}` },
                    { value: 'validation', title: `${GetTaskStatusChinese(TaskStatus.VALIDATION)}` },
                    { value: 'completed', title: `${GetTaskStatusChinese(TaskStatus.COMPLETED)}` },
                ],
            },
        },
        mode: {
            label: '数据类型',
            type: 'select',
            valueSources: ['value'],
            fieldSettings: {
                listValues: [
                    { value: 'interpolation', title: `${GetDataTypeChinese(DataType.VIDEO)}` },
                    { value: 'annotation', title: `${GetDataTypeChinese(DataType.IMAGE)}` },
                ],
            },
        },
        subset: {
            label: '用途',
            type: 'text',
            valueSources: ['value'],
            operators: ['equal'],
        },
        assignee: {
            label: '受托人',
            type: 'select',
            valueSources: ['value'],
            operators: ['select_equals'],
            fieldSettings: {
                useAsyncSearch: true,
                forceAsyncSearch: true,
                asyncFetch: asyncFetchUsers,
            },
        },
        owner: {
            label: '拥有者',
            type: 'select',
            valueSources: ['value'],
            operators: ['select_equals'],
            fieldSettings: {
                useAsyncSearch: true,
                forceAsyncSearch: true,
                asyncFetch: asyncFetchUsers,
            },
        },
        updated_date: {
            label: '更新时间',
            type: 'datetime',
            operators: ['between', 'greater', 'greater_or_equal', 'less', 'less_or_equal'],
        },
        id: {
            label: '任务标识',
            type: 'number',
            operators: ['equal', 'between', 'greater', 'greater_or_equal', 'less', 'less_or_equal'],
            fieldSettings: { min: 0 },
            valueSources: ['value'],
        },
        project_id: {
            label: '项目标识',
            type: 'number',
            operators: ['equal', 'between', 'greater', 'greater_or_equal', 'less', 'less_or_equal'],
            fieldSettings: { min: 0 },
            valueSources: ['value'],
        },
        name: {
            label: '任务名称',
            type: 'text',
            valueSources: ['value'],
            operators: ['like'],
        },
        project_name: {
            label: '项目名称',
            type: 'text',
            valueSources: ['value'],
            operators: ['like'],
        },
    },
};

export const localStorageRecentCapacity = 10;
export const localStorageRecentKeyword = 'recentlyAppliedTasksFilters';
export const predefinedFilterValues = {
    我创建的: '{"and":[{"==":[{"var":"owner"},"<username>"]}]}',
    我审核的: '{"and":[{"==":[{"var":"auditor"},"<username>"]}]}',
    我负责的: '{"and":[{"==":[{"var":"supervisor"},"<username>"]}]}',
    分配给我: '{"and":[{"==":[{"var":"assignee"},"<username>"]}]}',
    未完成的: '{"!":{"and":[{"==":[{"var":"status"},"completed"]}]}}',
    已锁定的: '{"and":[{"==":[{"var":"locked"},"True"]}]}',
};
